/**
 * AGROMX Frontend - Main Entry Point
 * Punto de entrada principal de la aplicación
 */

import authService from './services/auth.service.js';
import router from './router/router.js';
import toast from './utils/toast.js';
import modal from './utils/modal.js';
import pushService from './services/push.service.js';
import permissionsService from './services/permissions.service.js';
import { pintarVersion, mostrarNovedadesPendientes } from './utils/novedades.js';

// Importar vistas
import LoginView from './views/Login.js';
import TractoresListView from './views/TractoresList.js';
import RecepcionesListView from './views/RecepcionesList.js';
import RecepcionOCDetalleView from './views/RecepcionOCDetalle.js';
import NuevaRecepcionView from './views/NuevaRecepcion.js';
import DetalleRecepcionView from './views/DetalleRecepcion.js';
import PrintRecepcionView from './views/PrintRecepcion.js';
import ModelosListView from './views/ModelosList.js';
import AlmacenesListView from './views/AlmacenesList.js';

// Sistema de Compras
import RequisicionesListView from './views/RequisicionesList.js';
import OrdenesCompraListView from './views/OrdenesCompraList.js';
import ComprasListView from './views/ComprasList.js';
import CompraFormView from './views/CompraForm.js';
import CompraDetalleView from './views/CompraDetalle.js';
import PedidosListView from './views/PedidosList.js';
import PedidoDetalleView from './views/PedidoDetalle.js';

// Módulo de Producción
import ChecklistsListView from './views/ChecklistsList.js';
import ChecklistDetalleView from './views/ChecklistDetalle.js';
import OrdenesTrabajoListView from './views/OrdenesTrabajoList.js';
import OrdenTrabajoDetalleView from './views/OrdenTrabajoDetalle.js';
import PrintOrdenTrabajoView from './views/PrintOrdenTrabajo.js';
import PrintChecklistEnsambleView from './views/PrintChecklistEnsamble.js';
import LiberacionesListView from './views/LiberacionesList.js';
import OrdenesProduccionListView from './views/OrdenesProduccionList.js';
import OrdenProduccionDetalleView from './views/OrdenProduccionDetalle.js';
import LiberacionDetalleView from './views/LiberacionDetalle.js';

// Módulo de Ventas
import EntregasListView from './views/EntregasList.js';
import EntregaDetalleView from './views/EntregaDetalle.js';
import PrintEntregaView from './views/PrintEntrega.js';
import EncuestasListView from './views/EncuestasList.js';
import EncuestaDetalleView from './views/EncuestaDetalle.js';
import CertificadosListView from './views/CertificadosList.js';
import CertificadoDetalleView from './views/CertificadoDetalle.js';
import ChecklistDocumentosListView from './views/ChecklistDocumentosList.js';
import ChecklistDocumentosDetalleView from './views/ChecklistDocumentosDetalle.js';

// Módulo de Posventa
import PolizasListView from './views/PolizasList.js';
import PolizaDetalleView from './views/PolizaDetalle.js';
import CarnetsListView from './views/CarnetsList.js';
import CarnetDetalleView from './views/CarnetDetalle.js';
import EncuestasProductoListView from './views/EncuestasProductoList.js';
import EncuestaProductoDetalleView from './views/EncuestaProductoDetalle.js';
import ReportesGarantiaListView from './views/ReportesGarantiaList.js';
import ReporteGarantiaDetalleView from './views/ReporteGarantiaDetalle.js';

// Módulo de Calidad
import PNCListView from './views/PNCList.js';
import PNCDetalleView from './views/PNCDetalle.js';

// Dashboard
import DashboardView from './views/Dashboard.js';

// Administración
import UsuariosListView from './views/UsuariosList.js';
import RolesListView from './views/RolesList.js';
import RolePermisosMatrizView from './views/RolePermisosMatriz.js';

// Tracking GPS
import TrackingActivosView from './views/TrackingActivos.js';
import TrackingDetalleView from './views/TrackingDetalle.js';

// Catálogos - Personas
import PersonasListView from './views/catalogos/PersonasList.js';
import ProveedoresListView from './views/catalogos/ProveedoresList.js';
import PersonaFormView from './views/catalogos/PersonaForm.js';
import PersonaDetalleView from './views/catalogos/PersonaDetalle.js';
import LineasComercialesListView from './views/catalogos/LineasComercialesList.js';
import RemisionesListView from './views/RemisionesList.js';
import RemisionDetalleView from './views/RemisionDetalle.js';
import PrintRemisionView from './views/PrintRemision.js';

// Exponer utilidades globalmente
window.toast = toast;
window.modal = modal;
window.router = router;
window.permisos = permissionsService;

class App {
  constructor() {
    this.loading = document.getElementById('loading');
    this.app = document.getElementById('app');
    this.content = document.getElementById('content');
    this.navbar = document.getElementById('navbarUser');
    this.sidebar = document.getElementById('sidebar');
  }

  async init() {
    // Configurar router
    router.setContentElement(this.content);
    
    // Registrar rutas
    router.register('/', LoginView);
    // ⚠️ Antes de '/tractores', o el listado atrapa la ruta primero.
    router.register('/tractores/:numSerie/historia', async () => (await import('./views/TractorHistoria.js')).default);
    router.register('/tractores', TractoresListView);
    router.register('/almacen/recepciones', RecepcionesListView);
    router.register('/almacen/recepciones/pedido/:id', RecepcionOCDetalleView);
    router.register('/almacen/recepciones/nueva', NuevaRecepcionView);
    router.register('/almacen/recepciones/:id/print', PrintRecepcionView);
    router.register('/almacen/recepciones/:id', DetalleRecepcionView);
    router.register('/catalogos/modelos', ModelosListView);
    router.register('/catalogos/almacenes', AlmacenesListView);

    // Catálogos - Personas (rutas específicas antes que rutas con parámetros)
    router.register('/catalogos/personas/nueva', PersonaFormView);
    router.register('/catalogos/personas/:id/editar', PersonaFormView);
    router.register('/catalogos/personas/:id', PersonaDetalleView);
    router.register('/catalogos/personas', PersonasListView);
    router.register('/catalogos/proveedores', ProveedoresListView);
    router.register('/catalogos/lineas-comerciales', LineasComercialesListView);

    // Ventas - Remisiones
    router.register('/ventas/remisiones/nueva', RemisionDetalleView);
    router.register('/ventas/remisiones/:id/print', PrintRemisionView);
    router.register('/ventas/remisiones/:id', RemisionDetalleView);
    router.register('/ventas/remisiones', RemisionesListView);

    // Sistema de Compras
    router.register('/compras/pedidos/nuevo', PedidoDetalleView);
    router.register('/compras/pedidos/:id/print', async () => (await import('./views/PrintPedido.js')).default);
    router.register('/compras/pedidos/:id', PedidoDetalleView);
    router.register('/compras/pedidos', PedidosListView);
    router.register('/compras/requisiciones', RequisicionesListView);
    router.register('/compras/ordenes', OrdenesCompraListView);
    router.register('/compras/ordenes/nueva', OrdenesCompraListView);
    router.register('/compras', ComprasListView);
    router.register('/compras/nueva', CompraFormView);
    router.register('/compras/:id', CompraDetalleView);

    // Administración
    router.register('/admin/usuarios', UsuariosListView);
    router.register('/admin/roles', RolesListView);
    router.register('/admin/roles/:id/permisos', RolePermisosMatrizView);

    // Tracking GPS
    router.register('/admin/tracking/:id', TrackingDetalleView);
    router.register('/admin/tracking', TrackingActivosView);

    // Módulo de Producción (rutas específicas antes que rutas con parámetros)
    router.register('/produccion/checklists/nuevo', ChecklistDetalleView);
    router.register('/produccion/checklists/:id/print', PrintChecklistEnsambleView);
    router.register('/produccion/checklists/:id', ChecklistDetalleView);
    router.register('/produccion/checklists', ChecklistsListView);
    router.register('/produccion/ordenes-trabajo/nueva', OrdenTrabajoDetalleView);
    router.register('/produccion/ordenes-trabajo/:id/print', PrintOrdenTrabajoView);
    router.register('/produccion/ordenes-trabajo/:id', OrdenTrabajoDetalleView);
    router.register('/produccion/ordenes-trabajo', OrdenesTrabajoListView);
    // Órdenes de PRODUCCIÓN (ensamble en planta). Ojo: distintas de las
    // Órdenes de Trabajo de arriba, que son de posventa.
    router.register('/produccion/ordenes-produccion/:id', OrdenProduccionDetalleView);
    router.register('/produccion/ordenes-produccion', OrdenesProduccionListView);
    router.register('/produccion/liberaciones/nueva', LiberacionDetalleView);
    router.register('/produccion/liberaciones/:id/print', async () => (await import('./views/PrintLiberacion.js')).default);
    router.register('/produccion/liberaciones/:id', LiberacionDetalleView);
    router.register('/produccion/liberaciones', LiberacionesListView);

    // Módulo de Ventas (rutas específicas antes que rutas con parámetros)
    router.register('/ventas/entregas/nueva', EntregaDetalleView);
    router.register('/ventas/entregas/:id/print', PrintEntregaView);
    router.register('/ventas/entregas/:id', EntregaDetalleView);
    router.register('/ventas/entregas', EntregasListView);
    router.register('/ventas/encuestas/nueva', EncuestaDetalleView);
    router.register('/ventas/encuestas/:id/print', async () => (await import('./views/PrintEncuesta.js')).default);
    router.register('/ventas/encuestas/:id', EncuestaDetalleView);
    router.register('/ventas/encuestas', EncuestasListView);
    router.register('/ventas/certificados/nuevo', CertificadoDetalleView);
    router.register('/ventas/certificados/:id/print', async () => (await import('./views/PrintCertificado.js')).default);
    router.register('/ventas/certificados/:id', CertificadoDetalleView);
    router.register('/ventas/certificados', CertificadosListView);
    router.register('/ventas/checklist-entrega/nuevo', ChecklistDocumentosDetalleView);
    router.register('/ventas/checklist-entrega/:id/print', async () => (await import('./views/PrintChecklistDocumentos.js')).default);
    router.register('/ventas/checklist-entrega/:id', ChecklistDocumentosDetalleView);
    router.register('/ventas/checklist-entrega', ChecklistDocumentosListView);

    // Módulo de Posventa (rutas específicas antes que rutas con parámetros)
    router.register('/posventa/polizas/nueva', PolizaDetalleView);
    router.register('/posventa/polizas/:id/print', async () => (await import('./views/PrintPoliza.js')).default);
    router.register('/posventa/polizas/:id', PolizaDetalleView);
    router.register('/posventa/polizas', PolizasListView);
    router.register('/posventa/carnets/nuevo', CarnetDetalleView);
    router.register('/posventa/carnets/:id/print', async () => (await import('./views/PrintCarnet.js')).default);
    router.register('/posventa/carnets/:id', CarnetDetalleView);
    router.register('/posventa/carnets', CarnetsListView);
    router.register('/posventa/encuestas-producto/nueva', EncuestaProductoDetalleView);
    router.register('/posventa/encuestas-producto/:id/print', async () => (await import('./views/PrintEncuestaProducto.js')).default);
    router.register('/posventa/encuestas-producto/:id', EncuestaProductoDetalleView);
    router.register('/posventa/encuestas-producto', EncuestasProductoListView);
    router.register('/posventa/reportes-garantia/nuevo', ReporteGarantiaDetalleView);
    router.register('/posventa/reportes-garantia/:id/print', async () => (await import('./views/PrintReporteGarantia.js')).default);
    router.register('/posventa/reportes-garantia/:id', ReporteGarantiaDetalleView);
    router.register('/posventa/reportes-garantia', ReportesGarantiaListView);

    // Módulo de Calidad (rutas específicas antes que rutas con parámetros)
    router.register('/calidad/pnc/nuevo', PNCDetalleView);
    router.register('/calidad/pnc/:id/print', async () => (await import('./views/PrintPNC.js')).default);
    router.register('/calidad/pnc/:id', PNCDetalleView);
    router.register('/calidad/pnc', PNCListView);

    // Dashboard
    router.register('/dashboard', DashboardView);

    // ¿Qué hay de nuevo? (C-337): para todos los usuarios con sesión
    router.register('/novedades', async () => (await import('./views/Novedades.js')).default);

    // Versión en el pie de pantalla (C-337); también en el login
    pintarVersion();

    // Configurar enlaces con data-link
    router.setupLinks();

    // Verificar autenticación
    if (authService.isAuthenticated()) {
      await this.loadAuthenticatedApp();
    } else {
      await this.loadLoginPage();
    }

    // Ocultar loading
    this.loading.style.display = 'none';
    this.app.style.display = 'flex';

    // Prompt de instalación PWA (siempre, incluso en login)
    this.setupPWAInstallPrompt();

    // Manejar ruta inicial
    if (this._sinPermisos) {
      document.getElementById('content').innerHTML = `
        <div style="display: flex; justify-content: center; align-items: center; height: 60vh;">
          <div style="text-align: center; color: var(--primary);">
            <h2>Sin módulos asignados</h2>
            <p>Contacta al administrador para que te asigne permisos.</p>
          </div>
        </div>
      `;
    } else {
      router.handleRoute();
    }

    // Novedades de la versión (C-337): sólo con sesión; sale una vez por versión.
    if (authService.isAuthenticated()) {
      mostrarNovedadesPendientes();
    }
  }

  async loadAuthenticatedApp() {
    // Marca de notificaciones tracking: primera carga autenticada sin login reciente
    try {
      if (!sessionStorage.getItem('agromx_tracking_notify_since')) {
        sessionStorage.setItem('agromx_tracking_notify_since', new Date().toISOString());
      }
    } catch (e) {
      /* ignore */
    }

    // Obtener usuario actual
    const user = authService.getUser();

    // Renderizar navbar con usuario
    this.navbar.innerHTML = `
      <div class="navbar-user-info">
        <div class="navbar-user-name">${user.nombre}</div>
        <div class="navbar-user-role">${user.rol}</div>
      </div>
      <button class="navbar-logout" onclick="handleLogout()">
        Cerrar Sesión
      </button>
    `;

    // Renderizar sidebar con menú dinámico desde BD para todos los roles.
    // El admin se marca con setAdmin() (todos los permisos), pero los grupos
    // del menú salen del backend para que módulos nuevos aparezcan automático
    // sin tocar código.
    await permissionsService.load();
    if (user.rol === 'admin') {
      permissionsService.setAdmin();
    }
    this.renderMenuDinamico();

    // Mostrar sidebar
    this.sidebar.classList.add('visible');

    // Push: esperar registro para que /push/subscribe reactive activo=1 tras sesion nueva
    try {
      await pushService.init();
    } catch (err) {
      console.warn('[Push] No disponible:', err?.message || err);
    }
    // Reintento: si el navegador ya tenia suscripcion FCM pero fallo el POST (race SW, red), re-enviar al backend
    setTimeout(() => {
      pushService.syncSubscriptionToServer().catch((e) => console.warn('[Push] Re-sync:', e?.message || e));
    }, 1200);

    // Redirigir a página inicial según rol si está en login
    if (window.location.pathname === '/') {
      if (user.rol === 'admin') {
        router.navigate('/dashboard');
      } else {
        // Para roles dinámicos, ir al primer módulo disponible
        const primerRuta = this._primerRutaDisponible;
        if (primerRuta) {
          router.navigate(primerRuta);
        } else {
          this._sinPermisos = true;
        }
      }
    }
  }

  // ⚠️ DEPRECATED: el sidebar admin ahora se construye con renderMenuDinamico()
  // desde la BD (vía permissionsService.grupos) para que módulos nuevos aparezcan
  // automáticamente sin tocar código. Este método se conserva como fallback de
  // emergencia; está pendiente de eliminar en una sesión de cleanup.
  renderMenuAdmin() {
    this.sidebar.innerHTML = `
      <ul class="sidebar-menu">
        <li class="sidebar-menu-item">
          <a href="/dashboard" data-link class="sidebar-menu-link" style="display: flex; align-items: center; gap: 0.5rem; padding: 0.75rem 1rem; font-weight: bold; background: var(--primary); color: white; border-radius: 4px; margin-bottom: 0.5rem;">
            📊 Dashboard
          </a>
        </li>
        <li class="sidebar-menu-item">
          <button class="sidebar-submenu-toggle" onclick="toggleSubmenu('admin')">
            <span>⚙️ Administración</span>
            <span class="sidebar-submenu-icon">▶</span>
          </button>
          <ul class="sidebar-submenu" id="submenu-admin">
            <li class="sidebar-submenu-item">
              <a href="/admin/usuarios" data-link class="sidebar-submenu-link">
                👥 Usuarios
              </a>
            </li>
            <li class="sidebar-submenu-item">
              <a href="/admin/roles" data-link class="sidebar-submenu-link">
                🔐 Roles y Permisos
              </a>
            </li>
            <li class="sidebar-submenu-item">
              <a href="/admin/tracking" data-link class="sidebar-submenu-link">
                📡 Tracking
              </a>
            </li>
          </ul>
        </li>
        <li class="sidebar-menu-item">
          <button class="sidebar-submenu-toggle" onclick="toggleSubmenu('catalogos')">
            <span>📚 Catálogos</span>
            <span class="sidebar-submenu-icon">▶</span>
          </button>
          <ul class="sidebar-submenu" id="submenu-catalogos">
            <li class="sidebar-submenu-item">
              <a href="/catalogos/personas?rol=cliente" data-link class="sidebar-submenu-link">
                🛒 Clientes
              </a>
            </li>
            <li class="sidebar-submenu-item">
              <a href="/catalogos/proveedores" data-link class="sidebar-submenu-link">
                🏭 Proveedores
              </a>
            </li>
            <li class="sidebar-submenu-item">
              <a href="/catalogos/modelos" data-link class="sidebar-submenu-link">
                🚜 Modelos
              </a>
            </li>
            <li class="sidebar-submenu-item">
              <a href="/catalogos/almacenes" data-link class="sidebar-submenu-link">
                🏭 Almacenes
              </a>
            </li>
          </ul>
        </li>
        <li class="sidebar-menu-item">
          <button class="sidebar-submenu-toggle" onclick="toggleSubmenu('compras')">
            <span>🛒 Compras</span>
            <span class="sidebar-submenu-icon">▶</span>
          </button>
          <ul class="sidebar-submenu" id="submenu-compras">
            <li class="sidebar-submenu-item">
              <a href="/compras/pedidos" data-link class="sidebar-submenu-link">
                📄 Orden de Compra
              </a>
            </li>
          </ul>
        </li>
        <li class="sidebar-menu-item">
          <button class="sidebar-submenu-toggle" onclick="toggleSubmenu('almacen')">
            <span>📦 Almacén</span>
            <span class="sidebar-submenu-icon">▶</span>
          </button>
          <ul class="sidebar-submenu" id="submenu-almacen">
            <li class="sidebar-submenu-item">
              <a href="/tractores" data-link class="sidebar-submenu-link">
                🚜 Tractores
              </a>
            </li>
            <li class="sidebar-submenu-item">
              <a href="/almacen/recepciones" data-link class="sidebar-submenu-link">
                📦 Recepciones Visuales
              </a>
            </li>
          </ul>
        </li>
        <li class="sidebar-menu-item">
          <button class="sidebar-submenu-toggle" onclick="toggleSubmenu('produccion')">
            <span>⚙️ Producción</span>
            <span class="sidebar-submenu-icon">▶</span>
          </button>
          <ul class="sidebar-submenu" id="submenu-produccion">
            <li class="sidebar-submenu-item">
              <a href="/produccion/checklists" data-link class="sidebar-submenu-link">
                ✓ Check Lists Ensamble
              </a>
            </li>
            <li class="sidebar-submenu-item">
              <a href="/produccion/ordenes-produccion" data-link class="sidebar-submenu-link">
                🏭 Órdenes de Producción
              </a>
            </li>
            <li class="sidebar-submenu-item">
              <a href="/produccion/ordenes-trabajo" data-link class="sidebar-submenu-link">
                🔧 Órdenes de Trabajo
              </a>
            </li>
            <li class="sidebar-submenu-item">
              <a href="/produccion/liberaciones" data-link class="sidebar-submenu-link">
                🚜 Liberaciones
              </a>
            </li>
          </ul>
        </li>
        <li class="sidebar-menu-item">
          <button class="sidebar-submenu-toggle" onclick="toggleSubmenu('ventas')">
            <span>💰 Ventas</span>
            <span class="sidebar-submenu-icon">▶</span>
          </button>
          <ul class="sidebar-submenu" id="submenu-ventas">
            <li class="sidebar-submenu-item">
              <a href="/ventas/entregas" data-link class="sidebar-submenu-link">
                🚜 Entregas de Tractor
              </a>
            </li>
            <li class="sidebar-submenu-item">
              <a href="/ventas/encuestas" data-link class="sidebar-submenu-link">
                📊 Encuestas Satisfacción
              </a>
            </li>
            <li class="sidebar-submenu-item">
              <a href="/ventas/certificados" data-link class="sidebar-submenu-link">
                📜 Certificados de Venta
              </a>
            </li>
            <li class="sidebar-submenu-item">
              <a href="/ventas/checklist-entrega" data-link class="sidebar-submenu-link">
                📋 Checklist de Entrega
              </a>
            </li>
          </ul>
        </li>
        <li class="sidebar-menu-item">
          <button class="sidebar-submenu-toggle" onclick="toggleSubmenu('posventa')">
            <span>🔧 Posventa</span>
            <span class="sidebar-submenu-icon">▶</span>
          </button>
          <ul class="sidebar-submenu" id="submenu-posventa">
            <li class="sidebar-submenu-item">
              <a href="/posventa/polizas" data-link class="sidebar-submenu-link">
                📜 Pólizas de Garantía
              </a>
            </li>
            <li class="sidebar-submenu-item">
              <a href="/posventa/carnets" data-link class="sidebar-submenu-link">
                🔧 Carnets de Servicio
              </a>
            </li>
            <li class="sidebar-submenu-item">
              <a href="/posventa/encuestas-producto" data-link class="sidebar-submenu-link">
                📊 Encuestas Producto
              </a>
            </li>
            <li class="sidebar-submenu-item">
              <a href="/posventa/reportes-garantia" data-link class="sidebar-submenu-link">
                📋 Reportes de Garantía
              </a>
            </li>
          </ul>
        </li>
        <li class="sidebar-menu-item">
          <button class="sidebar-submenu-toggle" onclick="toggleSubmenu('calidad')">
            <span>✓ Calidad</span>
            <span class="sidebar-submenu-icon">▶</span>
          </button>
          <ul class="sidebar-submenu" id="submenu-calidad">
            <li class="sidebar-submenu-item">
              <a href="/calidad/pnc" data-link class="sidebar-submenu-link">
                📋 Productos No Conformes
              </a>
            </li>
          </ul>
        </li>
      </ul>
    `;
  }

  renderMenuDinamico() {
    this._primerRutaDisponible = null;

    const grupos = permissionsService.grupos;

    // «¿Qué hay de nuevo?» (C-337): fijo al final del menú, para todos.
    const novedadesHtml = `
      <li class="sidebar-menu-item sidebar-novedades">
        <a href="/novedades" data-link class="sidebar-menu-link sidebar-novedades-link" id="menuNovedades">
          ✨ ¿Qué hay de nuevo?
        </a>
      </li>
    `;

    if (!grupos.length) {
      this.sidebar.innerHTML = `
        <ul class="sidebar-menu">
          <li class="sidebar-menu-item" style="padding: 1rem; color: var(--gray-500); text-align: center;">
            No tienes módulos asignados. Contacta al administrador.
          </li>
          ${novedadesHtml}
        </ul>
      `;
      return;
    }

      // Los íconos vienen directo de BD (modulos.icono y modulo_grupos.icono)
      // como emojis. Si la BD no tiene ícono, se usa un fallback.
      const iconFor = (m) => m.icono || '•';
      const iconForGrupo = (g) => g.icono || '📁';

      // Guardar primera ruta disponible para redirección
      for (const grupo of grupos) {
        if (grupo.modulos?.length && grupo.modulos[0].ruta) {
          this._primerRutaDisponible = grupo.modulos[0].ruta;
          break;
        }
      }

      const menuHtml = grupos.map(grupo => {
        const grupoSlug = grupo.nombre.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '_');
        const modulosHtml = grupo.modulos.map(m =>
          `<li class="sidebar-submenu-item">
            <a href="${m.ruta}" data-link class="sidebar-submenu-link">
              <span style="margin-right:0.5rem;">${iconFor(m)}</span>${m.nombre}
            </a>
          </li>`
        ).join('');

        return `
          <li class="sidebar-menu-item">
            <button class="sidebar-submenu-toggle" onclick="toggleSubmenu('${grupoSlug}')">
              <span>${iconForGrupo(grupo)} ${grupo.nombre}</span>
              <span class="sidebar-submenu-icon">▶</span>
            </button>
            <ul class="sidebar-submenu" id="submenu-${grupoSlug}">
              ${modulosHtml}
            </ul>
          </li>
        `;
      }).join('');

    // Dashboard solo se muestra para admin (no es un módulo formal en BD —
    // es la home page con resumen general del sistema).
    const dashboardHtml = permissionsService.esAdmin ? `
      <li class="sidebar-menu-item">
        <a href="/dashboard" data-link class="sidebar-menu-link" style="display: flex; align-items: center; gap: 0.5rem; padding: 0.75rem 1rem; font-weight: bold; background: var(--primary); color: white; border-radius: 4px; margin-bottom: 0.5rem;">
          📊 Dashboard
        </a>
      </li>
    ` : '';

    this.sidebar.innerHTML = `
      <ul class="sidebar-menu">
        ${dashboardHtml}
        ${menuHtml}
        ${novedadesHtml}
      </ul>
    `;
  }

  setupPWAInstallPrompt() {
    let deferredPrompt = null;

    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      deferredPrompt = e;

      // Solo mostrar si no se ha descartado 3 veces hoy
      const dismissData = JSON.parse(localStorage.getItem('pwa-install-dismissed') || '{}');
      const hoy = new Date().toDateString();
      if (dismissData.date === hoy && dismissData.count >= 3) return;
      if (dismissData.date && dismissData.date !== hoy) {
        localStorage.removeItem('pwa-install-dismissed');
      }

      // Mostrar banner de instalación después de 3 segundos
      setTimeout(() => {
        if (!deferredPrompt) return;

        const banner = document.createElement('div');
        banner.id = 'pwa-install-banner';
        banner.innerHTML = `
          <div style="position:fixed; bottom:1rem; left:50%; transform:translateX(-50%); z-index:9999;
            background:#fff; border-radius:12px; box-shadow:0 4px 24px rgba(0,0,0,0.18);
            padding:1rem 1.25rem; display:flex; align-items:center; gap:1rem; max-width:420px; width:90%;">
            <img src="/img/Agromx-ico-01.png" alt="AGROMX" style="width:40px; height:40px; border-radius:8px;">
            <div style="flex:1;">
              <div style="font-weight:600; font-size:0.9rem;">Instalar AGROMX</div>
              <div style="font-size:0.78rem; color:#666;">Acceso directo y notificaciones del sistema</div>
            </div>
            <button id="pwa-install-btn" style="background:var(--primary); color:#fff; border:none; padding:0.4rem 1rem; border-radius:6px; font-size:0.82rem; cursor:pointer; white-space:nowrap;">Instalar</button>
            <button id="pwa-dismiss-btn" style="background:none; border:none; font-size:1.2rem; cursor:pointer; color:#999; padding:0 0.25rem;">&times;</button>
          </div>
        `;
        document.body.appendChild(banner);

        document.getElementById('pwa-install-btn').addEventListener('click', async () => {
          banner.remove();
          if (deferredPrompt) {
            deferredPrompt.prompt();
            const result = await deferredPrompt.userChoice;
            if (result.outcome === 'accepted') {
              toast.success('AGROMX instalada correctamente');
            }
            deferredPrompt = null;
          }
        });

        document.getElementById('pwa-dismiss-btn').addEventListener('click', () => {
          banner.remove();
          const data = JSON.parse(localStorage.getItem('pwa-install-dismissed') || '{}');
          const hoy = new Date().toDateString();
          localStorage.setItem('pwa-install-dismissed', JSON.stringify({
            date: hoy,
            count: (data.date === hoy ? data.count || 0 : 0) + 1
          }));
        });
      }, 3000);
    });
  }

  async loadLoginPage() {
    // Ocultar navbar y sidebar
    this.navbar.innerHTML = '';
    this.sidebar.classList.remove('visible');

    // Si no está en login, redirigir
    if (window.location.pathname !== '/') {
      router.navigate('/');
    }
  }
}

// Función global para logout
window.handleLogout = async () => {
  modal.confirm(
    'Cerrar Sesión',
    '¿Está seguro de que desea cerrar sesión?',
    async () => {
      permissionsService.clear();
      await authService.logout();
    }
  );
};

// Toggle sidebar (mobile)
window.toggleSidebar = () => {
  const sidebar = document.getElementById('sidebar');
  const overlay = document.getElementById('sidebarOverlay');
  const isOpen = sidebar.classList.toggle('open');
  if (overlay) overlay.classList.toggle('active', isOpen);
};

// Cerrar sidebar al navegar (mobile)
window.addEventListener('popstate', () => {
  const sidebar = document.getElementById('sidebar');
  const overlay = document.getElementById('sidebarOverlay');
  if (sidebar) sidebar.classList.remove('open');
  if (overlay) overlay.classList.remove('active');
});

// Función global para toggle de submenús
window.toggleSubmenu = (menuId) => {
  const submenu = document.getElementById(`submenu-${menuId}`);
  const toggle = submenu?.previousElementSibling;

  if (submenu && toggle) {
    submenu.classList.toggle('open');
    toggle.classList.toggle('open');
  }
};

// Iniciar aplicación cuando el DOM esté listo
document.addEventListener('DOMContentLoaded', () => {
  const app = new App();
  app.init();
});
